import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
const moodImages = ['https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=800&q=80', 'https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=800&q=80', 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=800&q=80', 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=800&q=80', 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&q=80', 'https://images.unsplash.com/photo-1531973576160-7125cd663d86?w=800&q=80'];
const motionPrinciples = [{
  title: 'Easing',
  body: 'cubic-bezier(0.32, 0.72, 0, 1) — confident in, gentle out. No overshoot. Never bouncy.'
}, {
  title: 'Duration ladder',
  body: '80ms (snap), 200ms (micro), 400ms (entry), 600ms (page). Anything longer is a story, not a transition.'
}, {
  title: 'Stagger',
  body: '30–50ms increments. Stagger implies causality — one thing causes the next, the eye follows the chain.'
}, {
  title: 'Hover & press',
  body: 'Hover settles in 150ms. Press snaps in 80ms. Pointer state changes are instant; result states are eased.'
}, {
  title: 'Network draw-in',
  body: 'Service-network lines render along their path on scroll-into-view. Stations appear after their connecting line lands.'
}, {
  title: 'Accessibility',
  body: 'Respect prefers-reduced-motion. Fall back to opacity. Motion is hierarchy, not decoration.'
}];
export function GraphicStyleSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section id="graphic" ref={ref} className="min-h-screen px-8 py-24" data-id="element-608">
      <motion.div initial={{
      opacity: 0,
      y: 40
    }} animate={isInView ? {
      opacity: 1,
      y: 0
    } : {}} transition={{
      duration: 0.5,
      ease: 'easeOut'
    }} className="max-w-7xl mx-auto" data-id="element-609">
        <div className="mb-16" data-id="element-610">
          <div className="font-mono text-xs text-[var(--text-muted)] tracking-wider mb-4" data-id="element-611">
            04 / GRAPHIC STYLE
          </div>
          <h2 className="font-space text-5xl md:text-6xl font-medium tracking-tight" data-id="element-612">
            Visual Language
          </h2>
        </div>

        <div className="space-y-20" data-id="element-613">
          {/* Service Network — reframed transit map */}
          <div data-id="element-614">
            <div className="flex items-baseline justify-between mb-6" data-id="element-615">
              <div className="font-mono text-xs text-[var(--text-muted)]" data-id="element-616">
                SIGNATURE MOTIF — SERVICE NETWORK
              </div>
              <div className="font-mono text-[10px] text-[var(--text-muted)]" data-id="element-617">
                04.1
              </div>
            </div>
            <div className="bg-[var(--bg-surface)] rounded-sm border border-[var(--divider)] p-10 overflow-hidden" data-id="element-618">
              <div className="mb-8 max-w-2xl" data-id="element-619">
                <div className="font-space text-2xl md:text-3xl font-medium leading-snug" data-id="element-620">
                  Capabilities that intersect.
                  <br data-id="element-621" />
                  <span className="text-[var(--text-muted)]" data-id="element-622">
                    Projects route through them.
                  </span>
                </div>
                <p className="font-helvetica text-sm text-[var(--text-muted)] mt-4 max-w-xl" data-id="element-623">
                  The transit map is not a map of subway stations. It is the
                  diagram of how disciplines connect inside the agency —
                  strategy, creative, production, performance. Lines are
                  capabilities. Stations are where they meet.
                </p>
              </div>

              <svg viewBox="0 0 900 320" className="w-full h-auto" data-id="element-624">
                {/* Architectural network — straighter, more deliberate than playful subway */}
                <path d="M 20 80 L 240 80 L 280 120 L 520 120 L 560 80 L 880 80" fill="none" stroke="var(--accent-blue)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" data-id="element-625" />
                <path d="M 20 160 L 200 160 L 240 200 L 600 200 L 640 160 L 880 160" fill="none" stroke="var(--accent-yellow)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" data-id="element-626" />
                <path d="M 20 240 L 320 240 L 360 200 L 720 200 L 760 240 L 880 240" fill="none" stroke="var(--accent-orange)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" data-id="element-627" />
                <path d="M 120 30 L 120 80 L 160 120 L 160 280" fill="none" stroke="var(--accent-green)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" data-id="element-628" />

                {/* Stations */}
                {[[240, 80, 'Strategy'], [520, 120, 'Creative'], [600, 200, 'Production'], [720, 200, 'Performance'], [160, 120, 'Brand'], [320, 240, 'Content']].map(([x, y, label]) => <g key={`${x}-${y}`} data-id="element-629">
                    <circle cx={x as number} cy={y as number} r="6" fill="var(--bg-surface)" stroke="var(--text-ink)" strokeWidth="2" data-id="element-630" />
                    <text x={x as number} y={(y as number) - 14} textAnchor="middle" className="font-mono" style={{
                  fontSize: '10px',
                  fill: 'var(--text-ink)'
                }} data-id="element-631">
                      {label}
                    </text>
                  </g>)}
              </svg>
            </div>
          </div>

          {/* New brand marks — replacing cartoon mascots */}
          <div data-id="element-632">
            <div className="flex items-baseline justify-between mb-6" data-id="element-633">
              <div className="font-mono text-xs text-[var(--text-muted)]" data-id="element-634">
                BRAND MARKS
              </div>
              <div className="font-mono text-[10px] text-[var(--text-muted)]" data-id="element-635">
                04.2
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-636">
              <div className="bg-[var(--bg-surface)] rounded-sm border border-[var(--divider)] p-10 flex flex-col items-center text-center" data-id="element-637">
                <svg viewBox="0 0 120 120" className="w-24 h-24 mb-6" data-id="element-638">
                  <circle cx="60" cy="60" r="50" fill="none" stroke="var(--accent-blue)" strokeWidth="1.5" opacity="0.3" data-id="element-639" />
                  <circle cx="60" cy="60" r="34" fill="none" stroke="var(--accent-blue)" strokeWidth="1.5" opacity="0.6" data-id="element-640" />
                  <circle cx="60" cy="60" r="18" fill="var(--accent-blue)" data-id="element-641" />
                  <line x1="60" y1="0" x2="60" y2="20" stroke="var(--accent-blue)" strokeWidth="1.5" data-id="element-642" />
                  <line x1="60" y1="100" x2="60" y2="120" stroke="var(--accent-blue)" strokeWidth="1.5" data-id="element-643" />
                  <line x1="0" y1="60" x2="20" y2="60" stroke="var(--accent-blue)" strokeWidth="1.5" data-id="element-644" />
                  <line x1="100" y1="60" x2="120" y2="60" stroke="var(--accent-blue)" strokeWidth="1.5" data-id="element-645" />
                </svg>
                <div className="font-space text-lg font-medium mb-2" data-id="element-646">
                  The Touchpoint
                </div>
                <div className="font-helvetica text-xs text-[var(--text-muted)] max-w-xs" data-id="element-647">
                  Concentric energy, four contact points. We're here, we're on
                  it.
                </div>
              </div>

              <div className="bg-[var(--bg-surface)] rounded-sm border border-[var(--divider)] p-10 flex flex-col items-center text-center" data-id="element-648">
                <svg viewBox="0 0 120 120" className="w-24 h-24 mb-6" data-id="element-649">
                  <line x1="20" y1="60" x2="100" y2="60" stroke="var(--text-ink)" strokeWidth="2" data-id="element-650" />
                  <line x1="60" y1="20" x2="60" y2="100" stroke="var(--text-ink)" strokeWidth="2" data-id="element-651" />
                  <line x1="30" y1="30" x2="90" y2="90" stroke="var(--accent-blue)" strokeWidth="2" data-id="element-652" />
                  <line x1="90" y1="30" x2="30" y2="90" stroke="var(--accent-blue)" strokeWidth="2" data-id="element-653" />
                  <circle cx="60" cy="60" r="6" fill="var(--bg-surface)" stroke="var(--accent-blue)" strokeWidth="2" data-id="element-654" />
                </svg>
                <div className="font-space text-lg font-medium mb-2" data-id="element-655">
                  The Cross-section
                </div>
                <div className="font-helvetica text-xs text-[var(--text-muted)] max-w-xs" data-id="element-656">
                  Disciplines crossing at one point. Where the work meets.
                </div>
              </div>

              <div className="bg-[var(--bg-surface)] rounded-sm border border-[var(--divider)] p-10 flex flex-col items-center text-center" data-id="element-657">
                <svg viewBox="0 0 120 120" className="w-24 h-24 mb-6" data-id="element-658">
                  <circle cx="30" cy="60" r="10" fill="var(--accent-blue)" opacity="0.35" data-id="element-659" />
                  <circle cx="60" cy="60" r="14" fill="var(--accent-blue)" opacity="0.65" data-id="element-660" />
                  <circle cx="95" cy="60" r="18" fill="var(--accent-blue)" data-id="element-661" />
                </svg>
                <div className="font-space text-lg font-medium mb-2" data-id="element-662">
                  The Pulse
                </div>
                <div className="font-helvetica text-xs text-[var(--text-muted)] max-w-xs" data-id="element-663">
                  Three beats, accelerating. Momentum, rhythm, progress.
                </div>
              </div>

              <div className="bg-[var(--bg-surface)] rounded-sm border border-[var(--divider)] p-10 flex flex-col items-center text-center" data-id="element-664">
                <svg viewBox="0 0 120 120" className="w-24 h-24 mb-6" data-id="element-665">
                  <rect x="20" y="20" width="80" height="80" fill="none" stroke="var(--accent-blue)" strokeWidth="1.5" opacity="0.3" data-id="element-666" />
                  <rect x="34" y="34" width="52" height="52" fill="none" stroke="var(--accent-blue)" strokeWidth="1.5" opacity="0.6" data-id="element-667" />
                  <rect x="48" y="48" width="24" height="24" fill="var(--accent-blue)" data-id="element-668" />
                </svg>
                <div className="font-space text-lg font-medium mb-2" data-id="element-669">
                  The Beacon
                </div>
                <div className="font-helvetica text-xs text-[var(--text-muted)] max-w-xs" data-id="element-670">
                  Concentric squares. Focus, broadcast, attention.
                </div>
              </div>

              <div className="bg-[var(--bg-surface)] rounded-sm border border-[var(--divider)] p-10 flex flex-col items-center text-center" data-id="element-671">
                <svg viewBox="0 0 120 120" className="w-24 h-24 mb-6" data-id="element-672">
                  <line x1="60" y1="10" x2="60" y2="110" stroke="var(--text-ink)" strokeWidth="2" data-id="element-673" />
                  <circle cx="60" cy="60" r="28" fill="var(--bg-surface)" stroke="var(--accent-blue)" strokeWidth="2" data-id="element-674" />
                  <circle cx="60" cy="60" r="5" fill="var(--accent-blue)" data-id="element-675" />
                </svg>
                <div className="font-space text-lg font-medium mb-2" data-id="element-676">
                  The Anchor
                </div>
                <div className="font-helvetica text-xs text-[var(--text-muted)] max-w-xs" data-id="element-677">
                  Vertical line through a held circle. Grounded, certain,
                  steady.
                </div>
              </div>

              <div className="bg-[var(--bg-surface)] rounded-sm border border-[var(--divider)] p-10 flex flex-col items-center text-center" data-id="element-678">
                <svg viewBox="0 0 120 120" className="w-24 h-24 mb-6" data-id="element-679">
                  <circle cx="45" cy="60" r="26" fill="none" stroke="var(--accent-blue)" strokeWidth="2" data-id="element-680" />
                  <circle cx="75" cy="60" r="26" fill="none" stroke="var(--text-ink)" strokeWidth="2" data-id="element-681" />
                </svg>
                <div className="font-space text-lg font-medium mb-2" data-id="element-682">
                  The Loop
                </div>
                <div className="font-helvetica text-xs text-[var(--text-muted)] max-w-xs" data-id="element-683">
                  Interlocking circles. Partnership, overlap, collaboration.
                </div>
              </div>
            </div>
          </div>

          {/* Visual Motifs — refreshed */}
          <div data-id="element-684">
            <div className="flex items-baseline justify-between mb-6" data-id="element-685">
              <div className="font-mono text-xs text-[var(--text-muted)]" data-id="element-686">
                VISUAL MOTIFS
              </div>
              <div className="font-mono text-[10px] text-[var(--text-muted)]" data-id="element-687">
                04.3
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-688">
              <div className="bg-[var(--bg-surface)] rounded-sm border border-[var(--divider)] p-8 flex flex-col items-center justify-center aspect-square" data-id="element-689">
                <svg viewBox="0 0 100 100" className="w-16 h-16 mb-4" data-id="element-690">
                  <path d="M 10 50 L 50 50 L 90 10" fill="none" stroke="var(--accent-blue)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" data-id="element-691" />
                  <circle cx="50" cy="50" r="5" fill="var(--bg-surface)" stroke="var(--text-ink)" strokeWidth="2" data-id="element-692" />
                </svg>
                <div className="text-xs font-mono text-[var(--text-muted)]" data-id="element-693">
                  Connector Line
                </div>
              </div>
              <div className="bg-[var(--bg-surface)] rounded-sm border border-[var(--divider)] p-8 flex flex-col items-center justify-center aspect-square" data-id="element-694">
                <div className="w-24 h-9 rounded-full bg-[var(--accent-blue)] mb-4" data-id="element-695" />
                <div className="text-xs font-mono text-[var(--text-muted)]" data-id="element-696">
                  Pill
                </div>
              </div>
              <div className="bg-[var(--bg-surface)] rounded-sm border border-[var(--divider)] p-8 flex flex-col items-center justify-center aspect-square relative" data-id="element-697">
                <div className="relative w-24 h-16 flex items-center justify-center mb-4" data-id="element-698">
                  <div className="absolute top-0 left-0 w-3 h-3 border-t-2 border-l-2 border-[var(--text-ink)]" data-id="element-699" />
                  <div className="absolute top-0 right-0 w-3 h-3 border-t-2 border-r-2 border-[var(--text-ink)]" data-id="element-700" />
                  <div className="absolute bottom-0 left-0 w-3 h-3 border-b-2 border-l-2 border-[var(--text-ink)]" data-id="element-701" />
                  <div className="absolute bottom-0 right-0 w-3 h-3 border-b-2 border-r-2 border-[var(--text-ink)]" data-id="element-702" />
                  <span className="font-space text-sm font-medium" data-id="element-703">focus</span>
                </div>
                <div className="text-xs font-mono text-[var(--text-muted)]" data-id="element-704">
                  Corner Brackets
                </div>
              </div>
              <div className="bg-[var(--bg-surface)] rounded-sm border border-[var(--divider)] p-8 flex flex-col items-center justify-center aspect-square overflow-hidden relative" data-id="element-705">
                <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full" data-id="element-706">
                  <circle cx="50" cy="50" r="40" fill="none" stroke="var(--accent-blue)" strokeWidth="1" opacity="0.3" data-id="element-707" />
                  <circle cx="50" cy="50" r="28" fill="none" stroke="var(--accent-blue)" strokeWidth="1" opacity="0.5" data-id="element-708" />
                  <circle cx="50" cy="50" r="16" fill="none" stroke="var(--accent-blue)" strokeWidth="1.5" data-id="element-709" />
                </svg>
                <div className="text-xs font-mono text-[var(--text-muted)] absolute bottom-8" data-id="element-710">
                  Halo Ring
                </div>
              </div>

              <div className="bg-[var(--bg-surface)] rounded-sm border border-[var(--divider)] p-8 flex flex-col items-center justify-center aspect-square" data-id="element-711">
                <svg viewBox="0 0 100 100" className="w-12 h-12 mb-4" data-id="element-712">
                  <line x1="50" y1="20" x2="50" y2="80" stroke="var(--text-ink)" strokeWidth="2" data-id="element-713" />
                  <line x1="20" y1="50" x2="80" y2="50" stroke="var(--text-ink)" strokeWidth="2" data-id="element-714" />
                </svg>
                <div className="text-xs font-mono text-[var(--text-muted)]" data-id="element-715">
                  Coordinate
                </div>
              </div>
              <div className="bg-[var(--bg-surface)] rounded-sm border border-[var(--divider)] p-8 flex flex-col items-center justify-center aspect-square" data-id="element-716">
                <div className="mb-4" data-id="element-717">
                  <span className="font-space text-2xl font-medium relative" data-id="element-718">
                    metro
                    <span className="absolute -bottom-1 left-0 right-0 h-1 bg-[var(--accent-blue)]" data-id="element-719" />
                  </span>
                </div>
                <div className="text-xs font-mono text-[var(--text-muted)]" data-id="element-720">
                  Underline
                </div>
              </div>
              <div className="bg-[var(--bg-surface)] rounded-sm border border-[var(--divider)] p-8 flex flex-col items-center justify-center aspect-square" data-id="element-721">
                <div className="grid grid-cols-4 gap-1.5 mb-4" data-id="element-722">
                  {Array.from({
                  length: 16
                }).map((_, i) => <div key={i} className="w-1 h-1 rounded-full bg-[var(--text-ink)]" data-id="element-723" />)}
                </div>
                <div className="text-xs font-mono text-[var(--text-muted)]" data-id="element-724">
                  Dot Grid
                </div>
              </div>
              <div className="bg-[var(--bg-surface)] rounded-sm border border-[var(--divider)] p-8 flex flex-col items-center justify-center aspect-square" data-id="element-725">
                <svg viewBox="0 0 100 100" className="w-16 h-16 mb-4" data-id="element-726">
                  <polygon points="30,30 70,50 30,70" fill="var(--accent-blue)" data-id="element-727" />
                </svg>
                <div className="text-xs font-mono text-[var(--text-muted)]" data-id="element-728">
                  Direction Wedge
                </div>
              </div>
            </div>
          </div>

          {/* Shape Language */}
          <div data-id="element-729">
            <div className="flex items-baseline justify-between mb-6" data-id="element-730">
              <div className="font-mono text-xs text-[var(--text-muted)]" data-id="element-731">
                SHAPE LANGUAGE
              </div>
              <div className="font-mono text-[10px] text-[var(--text-muted)]" data-id="element-732">
                04.4
              </div>
            </div>
            <div className="bg-[var(--bg-surface)] rounded-sm border border-[var(--divider)] p-12" data-id="element-733">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 items-end" data-id="element-734">
                <div className="text-center" data-id="element-735">
                  <div className="w-28 h-10 rounded-full bg-[var(--text-ink)] mb-4 mx-auto" data-id="element-736" />
                  <div className="text-xs font-mono text-[var(--text-muted)]" data-id="element-737">
                    Pill
                  </div>
                </div>
                <div className="text-center" data-id="element-738">
                  <div className="w-14 h-14 rounded-full bg-[var(--bg-surface)] border-[3px] border-[var(--text-ink)] mb-4 mx-auto" data-id="element-739" />
                  <div className="text-xs font-mono text-[var(--text-muted)]" data-id="element-740">
                    Station Dot
                  </div>
                </div>
                <div className="text-center" data-id="element-741">
                  <div className="w-28 h-1 bg-[var(--accent-blue)] rounded-full mb-4 mx-auto" data-id="element-742" />
                  <div className="text-xs font-mono text-[var(--text-muted)]" data-id="element-743">
                    Connector
                  </div>
                </div>
                <div className="text-center" data-id="element-744">
                  <div className="w-14 h-14 rounded-full border-2 border-[var(--accent-blue)] mb-4 mx-auto" data-id="element-745" />
                  <div className="text-xs font-mono text-[var(--text-muted)]" data-id="element-746">
                    Halo Ring
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Photography */}
          <div data-id="element-747">
            <div className="flex items-baseline justify-between mb-6" data-id="element-748">
              <div className="font-mono text-xs text-[var(--text-muted)]" data-id="element-749">
                PHOTOGRAPHY DIRECTION
              </div>
              <div className="font-mono text-[10px] text-[var(--text-muted)]" data-id="element-750">
                04.5
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-id="element-751">
              {moodImages.map((url, index) => <motion.div key={url} initial={{
              opacity: 0,
              scale: 0.95
            }} animate={isInView ? {
              opacity: 1,
              scale: 1
            } : {}} transition={{
              duration: 0.4,
              delay: index * 0.08
            }} className="aspect-[4/3] rounded-sm overflow-hidden border border-[var(--divider)]" data-id="element-752">
                  <img src={url} alt={`Mood ${index + 1}`} className="w-full h-full object-cover grayscale" data-id="element-753" />
                </motion.div>)}
            </div>
            <div className="mt-6 text-sm text-[var(--text-muted)] max-w-2xl font-helvetica" data-id="element-754">
              B&W documentary. Hands at work, real moments, no posed stock.
              Composition leans editorial — generous negative space, deliberate
              framing.
            </div>
          </div>

          {/* Motion Principles — rewritten */}
          <div data-id="element-755">
            <div className="flex items-baseline justify-between mb-6" data-id="element-756">
              <div className="font-mono text-xs text-[var(--text-muted)]" data-id="element-757">
                MOTION PRINCIPLES
              </div>
              <div className="font-mono text-[10px] text-[var(--text-muted)]" data-id="element-758">
                04.6
              </div>
            </div>
            <div className="bg-[var(--bg-surface)] rounded-sm border border-[var(--divider)] divide-y divide-[var(--divider)]" data-id="element-759">
              {motionPrinciples.map(principle => <div key={principle.title} className="p-6 grid grid-cols-1 md:grid-cols-12 gap-4" data-id="element-760">
                  <div className="md:col-span-3 font-space font-medium text-sm" data-id="element-761">
                    {principle.title}
                  </div>
                  <div className="md:col-span-9 font-helvetica text-sm text-[var(--text-muted)]" data-id="element-762">
                    {principle.body}
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </motion.div>
    </section>;
}